import TableContainer from "@mui/material/TableContainer";
import {Box, Paper} from "@mui/material";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import {AgGridReact} from "ag-grid-react";
import Grow from "@mui/material/Grow";
import React, { useEffect, useMemo, useRef } from 'react';
import { closeModal, openModal } from '../../components/Modal/modalReducer';
import { useDispatch, useSelector } from 'react-redux';
import { deleteTableView, updateTableView } from '../../store/actions/usersAction';
import ViewSplitButton from '../../components/ViewButton/ViewSplitButton';
import { updateAgGridState } from '../../store/actions';

const AGGridTable = ({
                         deleteHandler,
                         colDefs,
                         data,
                         updateHandler,
                         classes,
                         buttons,
                         onRowClicked,
                         onCellClicked,
                         gridRef,
                         onGridReady,
                         rowHeight,
                         getRowStyle,
                         autoSizeStrategy = {
                             type: 'fitCellContents',
                         },
                        rowGroupPanelShow,
                        groupDisplayType,
                        onTableSettingsChanged = (changed) => {},
                        tableSettingsChanged = false,
                        tableViewTypeName,
                        tableViews = []
                     }) => {
    const dispatch = useDispatch();
    const { dataPerTableName } = useSelector((state) => state.agGridState);
    const [selectedViewId, setSelectedViewId] = React.useState(null);
    gridRef ??= useRef();

    const onBtDelete = () => {
        // get the first child of the
        const selectedRows = gridRef.current.api.getSelectedRows();
        if (!selectedRows || selectedRows.length === 0) {
            console.log("No rows selected!");
            return;
        }

        deleteHandler(selectedRows, gridRef.current.api);
    };

    for (let i = 0; i < colDefs.length; i++) {
        if (colDefs[i].editable) {
            let originalFunction = colDefs[i].cellStyle
            colDefs[i].cellStyle = (params) => {
                if (!originalFunction) {
                    return {color: 'blue'}
                }

                let style = typeof originalFunction === 'function' ? originalFunction(params) : originalFunction
                if (style === null) {
                    style = {}
                }

                return {...style, color: 'blue'}
            }
        }
    }

    const dataTypeDefinitions = useMemo(() => {
        return {
            // override `date` to handle custom date format `dd/mm/yyyy`
            date: {
                baseDataType: 'date',
                extendsDataType: 'date',
                valueFormatter: params => {
                    if (params.value == null || params.value === '') {
                        return '';
                    }

                    const date = new Date(params.value);

                    return `${date.getDate()} ${date.toLocaleString('default', {month: 'short'})}, ${date.getFullYear()}`;
                },
            }
        };
    }, []);

    const onFirstDataRendered = () => {
      if (dataPerTableName[tableViewTypeName]) {
        applyTableConfig(dataPerTableName[tableViewTypeName]);
      }
    };

    const onTableChanged = () => {
      onTableSettingsChanged(true);
      // if there is no view selected, update in memory state
      if (selectedViewId === null) {
        dispatch(updateAgGridState({ [tableViewTypeName]: getTableConfigState() }));
      }
    };

    const getTableConfigState = () => {
      const gridApi = gridRef.current.api;
      return {
        columnState: gridApi.getColumnState(),
        filterModel: gridApi.getFilterModel(),
        columnGroupState: gridApi.getColumnGroupState()
      };
    };

    const onSaveView = () => {
      if (selectedViewId) {
        const selectedView = tableViews.find(it => it.id === selectedViewId);
        dispatch(updateTableView(selectedViewId, selectedView.name, getTableConfigState()))
      } else {
        dispatch(
          openModal({
            modalType: 'TableViewSaveModal',
            modalProps: {
              tableViewTypeName,
              tableConfig: getTableConfigState(),
              onSaved: (view) => {
                setSelectedViewId(view.id);
              }
            }
          })
        );
      }
    };

    const onViewClick = (view) => {
      // if selected again, reset state
      if (selectedViewId === view.id) {
        setSelectedViewId(null);

        if (dataPerTableName[tableViewTypeName]) {
          applyTableConfig(dataPerTableName[tableViewTypeName]);
        } else {
          resetTableState();
        }
      } else {
        const tableConfig = JSON.parse(view.config);
        setSelectedViewId(view.id);
        applyTableConfig(tableConfig);
      }
    };

    const onViewNameEdit = (view) => {
      dispatch(
        openModal({
          modalType: 'TableViewSaveModal',
          modalProps: {
            viewId: view.id,
            viewName: view.name,
            tableConfig: JSON.parse(view.config),
          }
        })
      )
    }

    const onViewDelete = (view) => {
      dispatch(
        openModal({
          modalType: 'AlertModal',
          modalProps: {
            title: "Delete view",
            content: `Are you sure you want to delete "${view.name}"?`,
            handleConfirm: () => dispatch(deleteTableView(view.id))
              .then(() => dispatch(closeModal()))
          }
        })
      )
    }

  const applyTableConfig = (tableConfig) => {
    const gridApi = gridRef.current.api;

    gridApi.applyColumnState({
      state: tableConfig.columnState,
      applyOrder: true
    });

    gridApi.setColumnGroupState(tableConfig.columnGroupState);
    gridApi.setFilterModel(tableConfig.filterModel);
  };

  const resetTableState = () => {
    const gridApi = gridRef.current.api;
    gridApi.resetColumnState();
    gridApi.setFilterModel(null);
    gridApi.resetColumnGroupState();
  };

  if (!buttons) {
        buttons = [];
    }
    if (deleteHandler) {
        buttons.push(<Button
            className={classes.ctaButton}
            startIcon={<Icon style={{color: '#5381EF'}}>delete</Icon>}
            onClick={onBtDelete}
        >
            Delete
        </Button>)
    }

    return (
        <Grow in>
            <TableContainer component={Paper} className={classes.paddingY}>
                {buttons && <Box sx={{display: 'flex', padding: '30px 0 0 30px'}}>
                    {buttons}

                    {tableSettingsChanged &&
                      <Button
                        className={classes.ctaButton}
                        startIcon={<Icon style={{color: '#5381EF'}}>save</Icon>}
                        onClick={onSaveView}
                        style={{ marginLeft: '10px' }}
                      >
                        Save View
                      </Button>
                    }
                </Box>}
                {tableViews.length > 0 && (
                  <Box sx={{padding: '0 30px 0 30px', width: "100%", overflowX: 'auto'}}>
                    {tableViews.map(view => (
                      <ViewSplitButton
                        key={view.id}
                        id={view.id}
                        name={view.name}
                        isActive={selectedViewId === view.id}
                        onSelect={() => onViewClick(view)}
                        options={[
                          {
                            text: 'Edit Name',
                            onSelected: () => onViewNameEdit(view)
                          },
                          {
                            text: 'Delete',
                            onSelected: () => onViewDelete(view)
                          }
                        ]}
                      />
                    ))}
                  </Box>
                )}
                <div
                    className="ag-theme-quartz" // applying the grid theme
                    style={{height: 560, margin: '30px'}} // the grid will fill the size of the parent container
                >
                    <AgGridReact
                        enableCharts={true}
                        onGridReady={onGridReady}
                        onFirstDataRendered={onFirstDataRendered}
                        groupDisplayType={groupDisplayType}
                        ref={gridRef}
                        columnDefs={colDefs}
                        rowData={data}
                        enableRangeSelection={true}
                        statusBar={{
                            statusPanels: [
                                {statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left'},
                                {
                                    statusPanel: 'agAggregationComponent', statusPanelParams: {
                                        aggFuncs: ['avg', 'sum']
                                    }
                                },
                            ]
                        }}
                        autoSizeStrategy={autoSizeStrategy}
                        onCellValueChanged={(value) => {
                            if (value.rowPinned === 'top') return;
                            updateHandler(value, gridRef.current.api)
                        }}
                        undoRedoCellEditing={true}
                        undoRedoCellEditingLimit={5}
                        suppressRowClickSelection={true}
                        onCellClicked={onCellClicked}
                        rowSelection={"multiple"}
                        dataTypeDefinitions={dataTypeDefinitions}
                        onRowClicked={onRowClicked}
                        getRowStyle={getRowStyle}
                        processDataFromClipboard={params => {
                            const emptyLastRow = params.data[params.data.length - 1][0] === ''
                                && params.data[params.data.length - 1].length === 1;

                            if (emptyLastRow) {
                                params.data.splice(params.data.length - 1, 1);
                            }

                            const lastIndex = gridRef.current.api.getModel().rowsToDisplay.length - 1;
                            const focusedCell = gridRef.current.api.getFocusedCell();
                            const focusedIndex = focusedCell.rowIndex;

                            if (focusedIndex + params.data.length - 1 > lastIndex) {
                                const resultLastIndex = focusedIndex + (params.data.length - 1);
                                const addRowCount = resultLastIndex - lastIndex;
                                let rowsToAdd = [];
                                let addedRows = 0;
                                let currIndex = params.data.length - 1;
                                while (addedRows < addRowCount) {
                                    rowsToAdd.push(params.data.splice(currIndex, 1)[0]);
                                    addedRows++;
                                    currIndex--;
                                }
                                rowsToAdd = rowsToAdd.reverse();
                                let newRowData = [];
                                rowsToAdd.map(r => {
                                    let row = {};
                                    let currColumn = focusedCell.column;
                                    r.map(i => {
                                        row[currColumn.colDef.field] = i;
                                        currColumn = gridRef.current.columnApi.getDisplayedColAfter(currColumn);
                                    });
                                    newRowData.push(row);
                                })
                                gridRef.current.api.updateRowData({add: newRowData});
                            }
                            return params.data;
                        }}
                        rowHeight={rowHeight}
                        rowGroupPanelShow={rowGroupPanelShow}
                        onColumnRowGroupChanged={onTableChanged}
                        onColumnPinned={onTableChanged}
                        onColumnVisible={onTableChanged}
                        onFilterChanged={onTableChanged}
                        onSortChanged={onTableChanged}
                        onColumnMoved={onTableChanged}
                    />
                </div>
            </TableContainer>
        </Grow>
    )
}

export default AGGridTable;
